import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  IconButton,
  Fab,
  Tooltip,
  Alert,
  CircularProgress,
  Container,
  Grid
} from '@mui/material';
import {
  Person,
  CalendarToday,
  VideoLibrary,
  PlayCircleFilled,
  ArrowBack,
  Publish,
  Visibility,
  AccessTime,
  MenuBook,
  Star,
  Bookmark,
  BookmarkBorder,
  Share,
  ExpandLess,
  Close,
  Fullscreen,
  FullscreenExit,
  ContentCopy,
  Check
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import coursesService from '../../services/CoursesService';
import teacherService from '../../services/TeacherService';
import CourseAccordion from '../IntroCourse/CourseAccordion';
import Description from '../IntroCourse/Description';
import Learn from '../IntroCourse/Learn';
import Comments from '../IntroCourse/Comments';
import CourseIncludes from '../IntroCourse/CourseIncludes';

const getEmbeddedInstructorName = (course) => {
  if (!course) return '';
  const embedded =
    course.teacherName ||
    course.TeacherName ||
    (typeof course.teacher === 'string' ? course.teacher : '') ||
    course.teacher?.name ||
    course.teacher?.nombre ||
    course.teacher?.Name;
  return (embedded || '').toString().trim();
};

const getCourseTeacherId = (course) => {
  if (!course) return null;
  const id =
    course.teacherId ??
    course.TeacherId ??
    course.teacher_id ??
    course.teached_id;
  if (id === undefined || id === null || id === '') return null;
  const num = Number(id);
  return Number.isNaN(num) ? null : num;
};

const getLoggedInUser = () => {
  try {
    return JSON.parse(localStorage.getItem('usuarioConCedula') || '{}');
  } catch {
    return {};
  }
};

const CoursePreview = ({ 
  course, 
  onClose, 
  onPublish, 
  isPreviewMode = true 
}) => {
  const navigate = useNavigate();
  const [isPublishing, setIsPublishing] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [instructorName, setInstructorName] = useState(() => getEmbeddedInstructorName(course));
  const [courseIdCopied, setCourseIdCopied] = useState(false);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const previewUser = getLoggedInUser();
  const courseId = course?.id ?? course?.Id ?? course?.courseId ?? null;

  // Set initial video when course loads
  useEffect(() => {
    if (course.videos && course.videos.length > 0) {
      setVideoSrc(course.videos[0].path);
      setCurrentVideoIndex(0);
    }
    setIsVideoLoading(false);
  }, [course]);

  useEffect(() => {
    let cancelled = false;

    const loadInstructor = async () => {
      const embedded = getEmbeddedInstructorName(course);
      if (embedded) {
        if (!cancelled) setInstructorName(embedded);
        return;
      }

      const teacherId = getCourseTeacherId(course);
      if (!teacherId) {
        if (!cancelled) setInstructorName('');
        return;
      }

      try {
        const teacherData = await teacherService.getTeacher(teacherId);
        const name =
          teacherData?.name ||
          teacherData?.nombre ||
          teacherData?.Name ||
          '';
        if (!cancelled) setInstructorName(name);
      } catch {
        const currentUser = getLoggedInUser();
        const currentUserId = Number(currentUser.id ?? currentUser.Id);
        const fallbackName =
          currentUserId === teacherId
            ? (currentUser.name || currentUser.nombre || currentUser.Name || '')
            : '';
        if (!cancelled) setInstructorName(fallbackName);
      }
    };

    loadInstructor();
    return () => {
      cancelled = true;
    };
  }, [course]);

  // Helper function to extract text from HTML
  const extractTextFromHtml = useCallback((htmlString) => {
    if (!htmlString) return "Sin título";
    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlString;
      return tempDiv.textContent || tempDiv.innerText || "Sin título";
    } catch (error) {
      return htmlString.replace(/<[^>]*>/g, '').trim() || "Sin título";
    }
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Fecha no disponible";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }, []);

  // Handle video selection
  const handleVideoSelect = useCallback((path, index) => {
    if (!path) return;
    
    setVideoSrc(path);
    setCurrentVideoIndex(index);
    setIsVideoLoading(true);
    setProgress(0);
    
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Handle video events
  const handleVideoProgress = useCallback((e) => {
    const video = e.target;
    if (video.duration > 0) {
      const progressPercent = (video.currentTime / video.duration) * 100;
      setProgress(progressPercent);
    }
  }, []);

  const handleVideoLoadStart = useCallback(() => {
    setIsVideoLoading(true);
  }, []);

  const handleVideoLoadedData = useCallback(() => {
    setIsVideoLoading(false);
  }, []);

  const handleVideoError = useCallback((e) => {
    console.error("Video error:", e);
    setIsVideoLoading(false);
  }, []);

  const handleVideoEnded = useCallback(() => {
    // Auto-play next video in preview mode
    const nextIndex = currentVideoIndex + 1;
    if (course.videos && nextIndex < course.videos.length) {
      handleVideoSelect(course.videos[nextIndex].path, nextIndex);
    }
  }, [course.videos, currentVideoIndex, handleVideoSelect]);

  const toggleFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video && !container) return;

    try {
      const isIos =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      if (isIos && typeof video?.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
        return;
      }

      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;

      if (fullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      const target = container || video;
      if (target.requestFullscreen) {
        await target.requestFullscreen();
      } else if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen();
      } else if (typeof video?.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
      }
    } catch (error) {
      console.error('Error al activar pantalla completa:', error);
    }
  }, []);

  // Prevenir descarga del video
  useEffect(() => {
    const video = videoRef.current;
    const container = videoContainerRef.current;

    if (video) {
      // Prevenir clic derecho (menú contextual)
      const preventContextMenu = (e) => {
        e.preventDefault();
        return false;
      };

      // Prevenir drag and drop
      const preventDrag = (e) => {
        e.preventDefault();
        return false;
      };

      // Prevenir atajos de teclado relacionados con descarga
      const preventKeyboardShortcuts = (e) => {
        // Prevenir Ctrl+S, Ctrl+Shift+I, F12, etc.
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          return false;
        }
        if ((e.ctrlKey || e.metaKey) && (e.shiftKey && (e.key === 'I' || e.key === 'i'))) {
          e.preventDefault();
          return false;
        }
        if (e.key === 'F12') {
          e.preventDefault();
          return false;
        }
      };

      // Prevenir selección de texto
      const preventSelection = (e) => {
        if (e.target === video || e.target === container) {
          e.preventDefault();
          return false;
        }
      };

      video.addEventListener('contextmenu', preventContextMenu);
      video.addEventListener('dragstart', preventDrag);
      video.addEventListener('selectstart', preventSelection);
      container?.addEventListener('contextmenu', preventContextMenu);
      container?.addEventListener('dragstart', preventDrag);
      container?.addEventListener('selectstart', preventSelection);
      document.addEventListener('keydown', preventKeyboardShortcuts);

      return () => {
        video.removeEventListener('contextmenu', preventContextMenu);
        video.removeEventListener('dragstart', preventDrag);
        video.removeEventListener('selectstart', preventSelection);
        container?.removeEventListener('contextmenu', preventContextMenu);
        container?.removeEventListener('dragstart', preventDrag);
        container?.removeEventListener('selectstart', preventSelection);
        document.removeEventListener('keydown', preventKeyboardShortcuts);
      };
    }
  }, [videoSrc, currentVideoIndex]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(
        document.fullscreenElement || document.webkitFullscreenElement
      ));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle publish action
  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      // Call the new publishCourse function with status 1
      await coursesService.publishCourse(course.id, 1, navigate);
      
      if (onPublish) {
        onPublish(course);
      }
    } catch (error) {
      console.error('Error publishing course:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const toggleBookmark = useCallback(() => {
    setIsBookmarked(prev => !prev);
  }, []);

  const handleCopyCourseId = useCallback(async () => {
    if (courseId == null || courseId === '') return;
    try {
      await navigator.clipboard.writeText(String(courseId));
      setCourseIdCopied(true);
      window.setTimeout(() => setCourseIdCopied(false), 2000);
    } catch {
      alert('No se pudo copiar el ID del curso. Inténtalo de nuevo.');
    }
  }, [courseId]);

  const handleShare = useCallback(() => {
    const courseTitle = extractTextFromHtml(course.title);
    const shareUrl = `${window.location.origin}/courses/getCourses?shared=${course.id}&title=${encodeURIComponent(courseTitle)}`;
    const text = `¡Mira este curso: ${courseTitle}`;

    if (navigator.share) {
      navigator.share({
        title: courseTitle,
        text,
        url: shareUrl
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          alert("¡Enlace del curso copiado al portapapeles!");
        })
        .catch(() => {
          alert("Error al copiar el enlace. Inténtalo de nuevo.");
        });
    }
  }, [course.id, course.title, extractTextFromHtml]);

  const courseTitle = extractTextFromHtml(course.title);
  const totalVideos = course.videos?.length || 0;
  const completedVideos = currentVideoIndex + 1;
  const courseProgress = totalVideos > 0 ? (completedVideos / totalVideos) * 100 : 0;
  const currentVideo = course.videos?.[currentVideoIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
      {/* Preview Header */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
        <Container maxWidth="xl">
          <div className="flex justify-between items-center py-3">
            <div className="flex gap-3 items-center">
              <Button
                startIcon={<ArrowBack />}
                onClick={onClose}
                sx={{ color: '#6b7280' }}
              >
                Volver
              </Button>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Vista Previa del Curso
              </Typography>
              {isPreviewMode && (
                <Chip 
                  label="MODO VISTA PREVIA" 
                  color="warning" 
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {isPreviewMode && courseId != null && courseId !== '' && (
                <Tooltip title={courseIdCopied ? '¡ID copiado!' : 'Clic para copiar el ID del curso'}>
                  <Chip
                    icon={courseIdCopied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                    label={`ID: ${courseId}`}
                    onClick={handleCopyCourseId}
                    clickable
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      letterSpacing: 0.3,
                      bgcolor: courseIdCopied ? '#e8f5e9' : '#e0f2f1',
                      color: courseIdCopied ? '#2e7d32' : '#00695c',
                      border: '1px solid',
                      borderColor: courseIdCopied ? '#81c784' : '#4DABA4',
                      '& .MuiChip-icon': {
                        color: courseIdCopied ? '#2e7d32' : '#4DABA4',
                      },
                    }}
                  />
                </Tooltip>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2 items-center">
              {isPreviewMode && course.status !== 1 && (
                <Button
                  variant="contained"
                  startIcon={<Publish />}
                  onClick={handlePublish}
                  disabled={isPublishing}
                  sx={{
                    backgroundColor: '#4DABA4',
                    '&:hover': { backgroundColor: '#3e958e' },
                    fontWeight: 600
                  }}
                >
                  {isPublishing ? 'Publicando...' : 'Publicar'}
                </Button>
              )}
              <IconButton onClick={toggleBookmark}>
                {isBookmarked ? <Bookmark className="text-yellow-500" /> : <BookmarkBorder />}
              </IconButton>
              <IconButton onClick={handleShare}>
                <Share />
              </IconButton>
            </div>
          </div>
        </Container>
      </div>

      {/* Mobile Header */}
      <div className="border-b shadow-sm backdrop-blur-sm lg:hidden bg-white/90">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <h1 className="flex-1 mr-4 text-lg font-bold text-gray-800 truncate">
              {courseTitle}
            </h1>
            <div className="flex items-center space-x-2">
              <IconButton onClick={toggleBookmark} size="small">
                {isBookmarked ? <Bookmark className="text-yellow-500" /> : <BookmarkBorder />}
              </IconButton>
              <IconButton size="small" onClick={handleShare}>
                <Share />
              </IconButton>
            </div>
          </div>

          {isPreviewMode && courseId != null && courseId !== '' && (
            <Box sx={{ mt: 1.5 }}>
              <Tooltip title={courseIdCopied ? '¡ID copiado!' : 'Clic para copiar el ID del curso'}>
                <Chip
                  icon={courseIdCopied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                  label={`ID del curso: ${courseId}`}
                  onClick={handleCopyCourseId}
                  clickable
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    bgcolor: courseIdCopied ? '#e8f5e9' : '#e0f2f1',
                    color: courseIdCopied ? '#2e7d32' : '#00695c',
                    border: '1px solid',
                    borderColor: courseIdCopied ? '#81c784' : '#4DABA4',
                    maxWidth: '100%',
                    '& .MuiChip-icon': {
                      color: courseIdCopied ? '#2e7d32' : '#4DABA4',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          )}
          
          {/* Progress bar */}
          <div className="mt-2">
            <div className="flex justify-between mb-1 text-xs text-gray-600">
              <span>Video {completedVideos} de {totalVideos}</span>
              <span>{Math.round(courseProgress)}%</span>
            </div>
            <LinearProgress 
              variant="determinate" 
              value={courseProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #4DABA4 0%, #3B82A1 100%)',
                }
              }}
            />
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex flex-col gap-8 lg:flex-row">
          
          {/* Left Column - Video and Content */}
          <div className="flex flex-col flex-1 gap-6">
            
            {/* Video Container */}
            <div className="relative">
              {videoSrc ? (
                <Card className="overflow-hidden bg-gradient-to-br from-gray-900 to-black shadow-2xl">
                  <div 
                    ref={videoContainerRef}
                    className={`course-preview-video-container relative w-full bg-gradient-to-br from-gray-900 to-black ${
                      isFullscreen ? 'h-screen w-screen pb-0' : 'pb-[56.25%]'
                    }`}
                    style={{
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onDoubleClick={toggleFullscreen}
                  >
                    {isVideoLoading && (
                      <div className="flex absolute inset-0 z-10 justify-center items-center bg-gray-900">
                        <div className="w-12 h-12 rounded-full border-b-2 border-blue-500 animate-spin"></div>
                      </div>
                    )}
                    
                    <video
                      ref={videoRef}
                      key={`${videoSrc}-${currentVideoIndex}`}
                      className={`absolute top-0 left-0 w-full h-full ${
                        isFullscreen ? 'object-contain' : 'object-cover'
                      }`}
                      controls
                      controlsList="nodownload noremoteplayback"
                      disablePictureInPicture
                      playsInline
                      onEnded={handleVideoEnded}
                      onTimeUpdate={handleVideoProgress}
                      onLoadStart={handleVideoLoadStart}
                      onLoadedData={handleVideoLoadedData}
                      onError={handleVideoError}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                      style={{ 
                        backgroundColor: "#111827",
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none'
                      }}
                      preload="metadata"
                    >
                      <source src={`${process.env.REACT_APP_LOCATION}${videoSrc}`} type="video/mp4" />
                      Tu navegador no soporta el elemento de video.
                    </video>

                    {!isFullscreen && (
                      <div className="absolute inset-x-0 top-0 h-[calc(100%-52px)] bg-gradient-to-t via-transparent pointer-events-none from-transparent to-black/10" />
                    )}
                  </div>

                  {/* Video info bar */}
                  <div className="px-4 py-3 text-white bg-gray-900">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <PlayCircleFilled className="text-blue-400" />
                        <div>
                          <p className="text-sm font-medium">
                            Video {currentVideoIndex + 1} de {totalVideos}
                          </p>
                          <p className="text-xs text-gray-400">
                            {extractTextFromHtml(currentVideo?.title) || 'Reproduciendo...'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Tooltip title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
                          <IconButton
                            size="small"
                            onClick={toggleFullscreen}
                            sx={{ color: 'white' }}
                            aria-label={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                          >
                            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                          </IconButton>
                        </Tooltip>
                        <div className="text-xs text-gray-400">
                          {Math.round(progress)}% completado
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card className="overflow-hidden shadow-2xl">
                  <div className="relative w-full pb-[56.25%] bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                    <div className="flex absolute inset-0 justify-center items-center">
                      <div className="text-center text-white">
                        <VideoLibrary className="mb-4 text-6xl opacity-50" />
                        <Typography variant="h6" className="opacity-75">
                          {totalVideos > 0 ? 'Selecciona un video para comenzar' : 'No hay videos disponibles'}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Desktop Course Title */}
            <div className="hidden lg:block">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h1 className="mb-2 text-3xl font-bold text-gray-900">
                    {courseTitle}
                  </h1>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Person className="text-sm" />
                      <span className="text-sm">
                        Instructor: {instructorName || 'Por asignar'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CalendarToday className="text-sm" />
                      <span className="text-sm">{formatDate(course.createdAt)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <VideoLibrary className="text-sm" />
                      <span className="text-sm">{totalVideos} videos</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Tabs */}
            <div className="lg:hidden">
              <div className="flex p-1 space-x-1 bg-white rounded-lg shadow-sm">
                {[
                  { id: 'description', label: 'Descripción', icon: MenuBook },
                  { id: 'learn', label: 'Aprenderás', icon: Star },
                  { id: 'comments', label: 'Comentarios', icon: Person }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center space-x-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <tab.icon className="text-xs" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Sections */}
            <div className="flex flex-col gap-6">
              {/* Desktop: Show all sections */}
              <div className="hidden lg:flex lg:flex-col lg:gap-6">
                <Description desc={course.description} />
                <Learn topics={course.labels || []} />
                <Comments courseId={course.id} />
              </div>

              {/* Mobile: Show active tab */}
              <div className="lg:hidden">
                {activeTab === 'description' && <Description desc={course.description} />}
                {activeTab === 'learn' && <Learn topics={course.labels || []} />}
                {activeTab === 'comments' && <Comments courseId={course.id} />}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="flex flex-col gap-6 w-full lg:w-1/3">
            
            {/* Course Progress Card */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <Typography variant="h6" className="font-bold text-gray-800">
                    Progreso del Curso
                  </Typography>
                  <Chip 
                    label={`${Math.round(courseProgress)}%`}
                    size="small"
                    sx={{ 
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)',
                      color: 'white'
                    }}
                  />
                </div>
                
                <LinearProgress 
                  variant="determinate" 
                  value={courseProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mb: 2,
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #4DABA4 0%, #3B82A1 100%)',
                    }
                  }}
                />
                
                <Typography variant="body2" color="text.secondary">
                  {completedVideos} de {totalVideos} videos completados
                </Typography>
              </CardContent>
            </Card>

            {/* Instructor Info */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center mb-4 space-x-4">
                  <Avatar 
                    sx={{ 
                      width: 50, 
                      height: 50,
                      background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)'
                    }}
                  >
                    <Person />
                  </Avatar>
                  <div>
                    <Typography variant="h6" className="font-bold text-gray-800">
                      {instructorName || 'Instructor'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Instructor del curso
                    </Typography>
                  </div>
                </div>
                
                <Divider className="my-4" />
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <CalendarToday className="text-xs" />
                    <span>Publicado: {formatDate(course.createdAt)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <AccessTime className="text-xs" />
                    <span>Duración: Flexible</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <VideoLibrary className="text-xs" />
                    <span>{totalVideos} videos</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Course Content Accordion */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <Typography variant="h6" className="mb-4 font-bold text-gray-800">
                  Contenido del Curso
                </Typography>
                <CourseAccordion 
                  videos={course.videos || []} 
                  documents={course.documents || []} 
                  questionnaires={course.questionnaires || []} 
                  onSelectVideo={handleVideoSelect}
                  currentVideoIndex={currentVideoIndex}
                  isPreviewMode={isPreviewMode}
                  courseTitle={courseTitle}
                  userName={previewUser.name || previewUser.nombre || instructorName || 'Vista previa'}
                  userCedula={previewUser.cedula || previewUser.Cedula || ''}
                  onQuizClick={(quizId, quizTitle) => {
                    const quizUrl = `/courses/millionaire-game/${quizId}?preview=1&courseId=${course.id}`;
                    window.open(quizUrl, '_blank', 'noopener,noreferrer');
                  }}
                />
              </CardContent>
            </Card>

            {/* Preview Actions */}
            {isPreviewMode && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <Typography variant="h6" className="mb-4 font-bold text-gray-800">
                    Acciones de Vista Previa
                  </Typography>
                  
                  <div className="space-y-3">
                    {course.status !== 1 && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<Publish />}
                        onClick={handlePublish}
                        disabled={isPublishing}
                        sx={{
                          backgroundColor: '#4DABA4',
                          '&:hover': { backgroundColor: '#3e958e' },
                          fontWeight: 600,
                          py: 1.5
                        }}
                      >
                        {isPublishing ? 'Publicando...' : 'Publicar Curso'}
                      </Button>
                    )}
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/curso/${course.id}`)}
                      sx={{
                        borderColor: '#4DABA4',
                        color: '#4DABA4',
                        '&:hover': { 
                          borderColor: '#3e958e',
                          backgroundColor: 'rgba(77, 171, 164, 0.1)' 
                        },
                        fontWeight: 600,
                        py: 1.5
                      }}
                    >
                      Ver Curso Completo
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Course Includes */}
            {Object.keys(course).length > 0 && (
              <CourseIncludes data={course} />
            )}
          </div>
        </div>
      </main>

      {/* Mobile FAB for course content */}
      <div className="fixed right-6 bottom-6 z-50 lg:hidden">
        <Fab 
          color="primary" 
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          sx={{
            background: 'linear-gradient(135deg, #4DABA4 0%, #3B82A1 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3B82A1 0%, #2563EB 100%)',
            }
          }}
        >
          <MenuBook />
        </Fab>
      </div>

      {/* Mobile Bottom Sheet */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden bg-black/50" onClick={() => setShowMobileMenu(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <Typography variant="h6" className="font-bold">
                  Contenido del Curso
                </Typography>
                <IconButton onClick={() => setShowMobileMenu(false)}>
                  <ExpandLess />
                </IconButton>
              </div>
            </div>
            <div className="p-4">
              <CourseAccordion 
                videos={course.videos || []} 
                documents={course.documents || []} 
                questionnaires={course.questionnaires || []} 
                onSelectVideo={(path, index) => {
                  handleVideoSelect(path, index);
                  setShowMobileMenu(false);
                }}
                currentVideoIndex={currentVideoIndex}
                isPreviewMode={isPreviewMode}
                courseTitle={courseTitle}
                userName={previewUser.name || previewUser.nombre || instructorName || 'Vista previa'}
                userCedula={previewUser.cedula || previewUser.Cedula || ''}
                onQuizClick={(quizId, quizTitle) => {
                  const quizUrl = `/courses/millionaire-game/${quizId}?preview=1&courseId=${course.id}`;
                  window.open(quizUrl, '_blank', 'noopener,noreferrer');
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursePreview; 